// pages/SolutionsDetail.js
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, ArrowLeft, MapPin } from 'lucide-react';

const SolutionsDetail = () => {
  const { slug } = useParams();

  const solutionsData = {
    'lv': {
      title: 'LV Systems',
      description: 'Low voltage distribution, VFDs, and MCC panels.',
      specs: '415V, VFDs, MCCs, Distribution Boards',
      image: '/images/solutions/lv-panel-building.jpg',
      details: 'Complete low voltage solutions including Variable Frequency Drives (VFDs) for motor control, Motor Control Centers (MCCs) for industrial processes, and custom distribution boards. We design, supply, install, and commission LV systems up to 415V.',
      features: ['VFD Installation & Programming', 'MCC Panel Design', 'Distribution Boards', 'Motor Control', 'Energy Optimization'],
      applications: ['Commercial Buildings', 'Industrial Plants', 'Pump Stations', 'HVAC Systems', 'Manufacturing'],
      brands: ['Schneider Electric', 'Siemens', 'ABB', 'Danfoss', 'Himel']
    },
    'mv': {
      title: 'MV Systems',
      description: 'Medium voltage switchgear and protection systems.',
      specs: '11kV, 33kV, Switchgear, Protection',
      image: '/images/solutions/mv-switchgear.jpg',
      details: 'Comprehensive medium voltage solutions including switchgear panels, protection relays, and substation automation. Specializing in 11kV and 33kV systems for industrial plants, utilities, and large commercial facilities.',
      features: ['MV Switchgear Supply', 'Protection Relays', 'Substation Automation', 'SCADA Integration', 'Commissioning'],
      applications: ['Substations', 'Industrial Plants', 'Utilities', 'Mining', 'Data Centers'],
      brands: ['ABB', 'Siemens', 'Schneider Electric']
    },
    'solar': {
      title: 'Solar Energy',
      description: 'Residential, commercial, and industrial solar solutions.',
      specs: '1kW - 1MW+, Grid-Tied, Off-Grid, Hybrid',
      image: '/images/solutions/solar-installation.jpg',
      details: 'End-to-end solar solutions from design to commissioning. Grid-tied, off-grid, and hybrid solar systems with high-efficiency PV panels, advanced battery storage, and smart monitoring for residential, commercial, and industrial applications.',
      features: ['PV System Design', 'Battery Storage', 'Hybrid Systems', 'Smart Monitoring', 'Maintenance'],
      applications: ['Residential', 'Commercial', 'Industrial', 'Remote Communities', 'Agriculture'],
      brands: ['Jinko Solar', 'Canadian Solar', 'Trina Solar', 'Schneider Electric']
    },
    'digital-energy': {
      title: 'Digital Energy',
      description: 'Smart monitoring, power quality meters, and analytics.',
      specs: 'Smart Monitoring, Power Quality, IoT, Analytics',
      image: '/images/solutions/digital-energy.jpg',
      details: 'Smart energy management solutions including Power Quality Meters, energy analytics, and IoT-enabled systems. Real-time data and actionable insights for better decision-making and energy savings.',
      features: ['Power Quality Meters', 'Energy Monitoring', 'Data Analytics', 'IoT Integration', 'Energy Optimization'],
      applications: ['Commercial Buildings', 'Data Centers', 'Utilities', 'Hospitals', 'Educational Institutions'],
      brands: ['Schneider Electric', 'Siemens', 'ABB', 'Eaton']
    },
    'automation': {
      title: 'Industrial Automation',
      description: 'PLC, SCADA, and control systems.',
      specs: 'PLC, SCADA, DCS, HMI, Control Systems',
      image: '/images/solutions/automation.jpg',
      details: 'Complete automation solutions including PLC programming, SCADA systems, and process control integration. From simple control panels to complex distributed control systems for improved efficiency and safety.',
      features: ['PLC Programming', 'SCADA Systems', 'Process Control', 'HMI Development', 'System Integration'],
      applications: ['Manufacturing', 'Water Treatment', 'Oil & Gas', 'Food Processing', 'Pharmaceuticals'],
      brands: ['Siemens', 'Schneider Electric', 'ABB', 'Rockwell Automation']
    },
    'backup-power': {
      title: 'Backup Power',
      description: 'UPS, Generators, ATS, and Battery Storage for critical loads.',
      specs: 'UPS, Generators, ATS, Battery Storage',
      image: '/images/solutions/backup-power.jpg',
      details: 'Complete backup power solutions including Uninterruptible Power Supplies (UPS), diesel/gas generators, Automatic Transfer Switches (ATS), and battery storage systems. Ensuring power continuity for critical loads in data centers, hospitals, industrial plants, and commercial buildings.',
      features: ['UPS Systems', 'Generator Supply & Installation', 'ATS Integration', 'Battery Storage', 'Load Management'],
      applications: ['Data Centers', 'Hospitals', 'Industrial Plants', 'Commercial Buildings', 'Critical Infrastructure'],
      brands: ['Schneider Electric', 'Cummins', 'Caterpillar', 'Siemens', 'Eaton']
    }
  };

  const solution = solutionsData[slug];

  if (!solution) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Solution not found</h1>
          <Link to="/solutions" className="text-[#006D77] hover:underline mt-2 inline-block text-sm">
            ← Back to Solutions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <section className="py-10 bg-gradient-to-r from-[#006D77] to-[#008B8B] text-white">
        <div className="container mx-auto px-4 md:px-6">
          <Link to="/solutions" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-3 transition-colors">
            <ArrowLeft size={16} className="mr-2" />
            Back to Solutions
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">{solution.title}</h1>
          <p className="text-white/80 text-sm mt-1 max-w-2xl">{solution.description}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs">{solution.specs}</span>
            <span className="flex items-center text-white/60 text-xs">
              <MapPin size={12} className="mr-1" />
              Kenya
            </span>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <img 
                src={solution.image}
                alt={solution.title}
                className="w-full h-56 object-cover rounded-lg shadow"
                onError={(e) => {
                  e.target.src = `https://picsum.photos/seed/${slug}/800/600`;
                }}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Overview</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{solution.details}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Key Features</h3>
              <ul className="space-y-1.5">
                {solution.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                    <Check size={14} className="text-[#006D77] flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Applications</h3>
              <ul className="space-y-1.5">
                {solution.applications.map((app, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                    <Check size={14} className="text-[#006D77] flex-shrink-0 mt-0.5" />
                    {app}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-bold text-gray-900 mb-2">Brands We Work With</h3>
            <div className="flex flex-wrap gap-1.5">
              {solution.brands.map((brand, idx) => (
                <span key={idx} className="bg-white px-3 py-1 rounded-full text-xs text-gray-700 shadow-sm border border-gray-100">
                  {brand}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#006D77] to-[#008B8B] rounded-lg p-5 text-center text-white">
            <h3 className="text-lg font-bold mb-1">Need This Solution?</h3>
            <p className="text-white/80 text-sm mb-3">Contact our expert team for a free consultation</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Link to="/contact" className="bg-white text-[#006D77] px-5 py-1.5 rounded-full font-semibold hover:bg-gray-100 transition-all text-sm">
                Get a Quote
              </Link>
              <a href="tel:+254735338408" className="border-2 border-white text-white px-5 py-1.5 rounded-full font-semibold hover:bg-white/10 transition-all text-sm">
                Call 0735 338 408
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsDetail;
